import React, { useState, useEffect } from "react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import Container from "@/components/ui/Container";
import { ArrowRight, Mail, Phone, MapPin, Clock, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formRateLimiter } from "@/lib/utils/rateLimit";
import { 
  validateContactForm, 
  type ContactFormData, 
  type ValidationResult,
  type ValidationError,
  type ValidationFailure,
  type SubscriberFormData 
} from "@/lib/utils/formValidation";
import { usePageTitle } from "@/hooks/usePageTitle";

const initialFormData: ContactFormData = {
  name: "",
  email: "",
  phone: "",
  company: "",
  message: ""
};

const isValidationFailure = (result: ValidationResult): result is ValidationFailure => {
  return !result.success;
};

const isContactFormData = (data: ContactFormData | SubscriberFormData): data is ContactFormData => {
  return 'name' in data && 'message' in data;
};

const ContactPage = () => {
  usePageTitle("Контакты");
  const [formData, setFormData] = useState<ContactFormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [remainingTime, setRemainingTime] = useState(0);

  useEffect(() => {
    // Check remaining time on component mount
    const time = formRateLimiter.getRemainingTime("contact");
    if (time > 0) {
      setRemainingTime(time);
      const timer = setInterval(() => {
        setRemainingTime(prev => Math.max(0, prev - 1000));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear validation error for the field being changed
    setValidationErrors(prev => prev.filter(error => error.field !== name));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check rate limiting
    if (!formRateLimiter.canSubmit("contact")) {
      const time = formRateLimiter.getRemainingTime("contact");
      setRemainingTime(time);
      toast.error(`Пожалуйста, подождите ${Math.ceil(time / 1000)} секунд перед повторной отправкой`);
      return;
    }

    // Validate form data
    const result = validateContactForm(formData);
    if (isValidationFailure(result)) {
      setValidationErrors(result.errors);
      toast.error("Пожалуйста, исправьте ошибки в форме");
      return;
    }

    setIsSubmitting(true);
    
    try {
      if (isContactFormData(result.data)) {
        await api.contact.sendMessage(result.data);
        toast.success("Ваше сообщение успешно отправлено! Мы свяжемся с вами в ближайшее время.");
        setIsSubmitted(true);
        setFormData(initialFormData);
      } else {
        throw new Error("Неверный формат данных формы");
      }
    } catch (error: any) {
      console.error("Error sending message:", error);
      toast.error(error.message || "Произошла ошибка при отправке сообщения. Пожалуйста, попробуйте позже.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <Phone className="h-5 w-5 text-primary" />,
      title: "Телефон",
      details: "+375 (29) 332-60-12",
      link: "tel:+375293326012"
    },
    {
      icon: <Mail className="h-5 w-5 text-primary" />,
      title: "Email",
      details: "info@stackadvance.io",
      link: "mailto:info@stackadvance.io"
    },
    {
      icon: <MapPin className="h-5 w-5 text-primary" />,
      title: "Адрес",
      details: "г. Минск, ул. Кульман, д. 9",
      link: "#"
    },
    {
      icon: <Clock className="h-5 w-5 text-primary" />,
      title: "Время работы",
      details: "Пн-Пт: 9:00 - 18:00",
      link: "#"
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 bg-secondary">
          
          <Container>
            <div className="max-w-3xl mx-auto text-center">
              <div className="tag mb-6 animate-fade-in">Свяжитесь с нами</div>
              <h1 className="heading text-4xl md:text-5xl font-bold mb-6 animate-fade-in" style={{ animationDelay: "0.2s" }}>
                Готовы обсудить <span className="text-gradient">ваш проект</span>?
              </h1>
              <p className="text-muted-foreground text-lg mb-0 animate-fade-in" style={{ animationDelay: "0.4s" }}>
                Напишите нам, и мы свяжемся с вами, чтобы обсудить, как мы можем помочь вашему бизнесу с помощью инновационных технологических решений.
              </p>
            </div>
          </Container>
        </section>

        {/* Contact Section */}
        <section className="py-20">
          <Container>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
              <div className="lg:col-span-2 space-y-6">
                <div className="glass rounded-2xl p-6">
                  <h2 className="text-2xl font-semibold mb-6">Наши контакты</h2>
                  <div className="space-y-6">
                    {contactInfo.map((item, index) => (
                      <div key={index} className="flex items-start">
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center mr-4 shrink-0">
                          {item.icon}
                        </div>
                        <div>
                          <h3 className="text-base font-semibold">{item.title}</h3>
                          <a 
                            href={item.link} 
                            className="text-muted-foreground hover:text-primary transition-colors"
                          >
                            {item.details}
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Social Links */}
                {/* <div className="glass rounded-2xl p-6">
                  <h2 className="text-lg font-semibold mb-4">Мы в социальных сетях</h2>
                  <div className="flex space-x-4">
                    <a
                      href="#"
                      className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-colors"
                      aria-label="LinkedIn"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-5 w-5"
                      >
                        <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                        <rect x="2" y="9" width="4" height="12"></rect>
                        <circle cx="4" cy="4" r="2"></circle>
                      </svg>
                    </a>
                    <a
                      href="#"
                      className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-colors"
                      aria-label="Twitter"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-5 w-5"
                      >
                        <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"></path>
                      </svg>
                    </a>
                    <a
                      href="#"
                      className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-colors"
                      aria-label="Facebook"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-5 w-5"
                      >
                        <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                      </svg>
                    </a>
                    <a
                      href="#"
                      className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-colors"
                      aria-label="Instagram"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-5 w-5"
                      >
                        <rect width="20" height="20" x="2" y="2" rx="5" ry="5"></rect>
                        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                        <line x1="17.5" x2="17.51" y1="6.5" y2="6.5"></line>
                      </svg>
                    </a>
                  </div>
                </div> */}
                {/* Map */}
                <div className="relative aspect-[4/3] bg-secondary rounded-2xl overflow-hidden">
                  <div className="absolute inset-0 bg-gray-200">
                    {/* Mock map display */}
                    <div className="absolute inset-0 bg-[url('https://email-images.mindbox.ru/Fashionhouse/af4b732c-5dae-4fdb-84f4-0ea2c7ad52f3/20298b46-2a95-429e-9aac-e5eabca257bf.png')] bg-cover bg-center opacity-30"></div>
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center animate-pulse">
                      <MapPin className="h-4 w-4" />
                    </div>
                  </div>
                  <div className="absolute inset-0 flex items-end justify-center pb-4">
                    <button 
                      onClick={() => window.open("https://yandex.by/maps/org/stack_advance/93373093742/?ll=27.580094%2C53.920990&z=17", "_blank")} 
                      className="bg-white text-foreground px-4 py-2 rounded-lg text-sm font-medium shadow-sm"
                    >
                      Открыть карту
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="lg:col-span-3">
                <div className="glass rounded-2xl p-6 md:p-8">
                  <h2 className="text-2xl font-semibold mb-6">Отправьте нам сообщение!</h2>
                  
                  {isSubmitted ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Check className="h-8 w-8 text-primary" />
                      </div>
                      <h3 className="text-xl font-semibold mb-2">Сообщение отправлено!</h3>
                      <p className="text-muted-foreground mb-6">
                        Спасибо за обращение. Мы свяжемся с вами в ближайшее время.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium mb-2">
                            Имя*
                          </label>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className={`w-full px-4 py-2 bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                              validationErrors.some(e => e.field === "name") ? "border-red-500" : "border-border"
                            }`}
                            placeholder="Введите ваше имя"
                          />
                          {validationErrors.some(e => e.field === "name") && (
                            <p className="mt-1 text-sm text-red-500">
                              {validationErrors.find(e => e.field === "name")?.message}
                            </p>
                          )}
                        </div>
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium mb-2">
                            Email*
                          </label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className={`w-full px-4 py-2 bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                              validationErrors.some(e => e.field === "email") ? "border-red-500" : "border-border"
                            }`}
                            placeholder="Введите ваш email"
                          />
                          {validationErrors.some(e => e.field === "email") && (
                            <p className="mt-1 text-sm text-red-500">
                              {validationErrors.find(e => e.field === "email")?.message}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div>
                          <label htmlFor="phone" className="block text-sm font-medium mb-2">
                            Телефон
                          </label>
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            className={`w-full px-4 py-2 bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                              validationErrors.some(e => e.field === "phone") ? "border-red-500" : "border-border"
                            }`}
                            placeholder="Введите ваш телефон"
                          />
                          {validationErrors.some(e => e.field === "phone") && (
                            <p className="mt-1 text-sm text-red-500">
                              {validationErrors.find(e => e.field === "phone")?.message}
                            </p>
                          )}
                        </div>
                        <div>
                          <label htmlFor="company" className="block text-sm font-medium mb-2">
                            Компания
                          </label>
                          <input
                            type="text"
                            id="company"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            className={`w-full px-4 py-2 bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                              validationErrors.some(e => e.field === "company") ? "border-red-500" : "border-border"
                            }`}
                            placeholder="Введите название компании"
                          />
                          {validationErrors.some(e => e.field === "company") && (
                            <p className="mt-1 text-sm text-red-500">
                              {validationErrors.find(e => e.field === "company")?.message}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="mb-6">
                        <label htmlFor="message" className="block text-sm font-medium mb-2">
                          Сообщение*
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          rows={5}
                          className={`w-full px-4 py-2 bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none ${
                            validationErrors.some(e => e.field === "message") ? "border-red-500" : "border-border"
                          }`}
                          placeholder="Расскажите о вашем проекте или вопросе"
                        ></textarea>
                        {validationErrors.some(e => e.field === "message") && (
                          <p className="mt-1 text-sm text-red-500">
                            {validationErrors.find(e => e.field === "message")?.message}
                          </p>
                        )}
                      </div>
                      
                      <button
                        type="submit"
                        disabled={isSubmitting || remainingTime > 0}
                        className="button-primary w-full justify-center py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center">
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Отправка...
                          </span>
                        ) : remainingTime > 0 ? (
                          <span className="flex items-center">
                            Подождите {Math.ceil(remainingTime / 1000)} сек
                          </span>
                        ) : (
                          <span className="flex items-center">
                            Отправить сообщение
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </span>
                        )}
                      </button>
                    </form>
                  )}
                </div>
                
                {/* FAQ */}
                <div className="mt-8 bg-white rounded-2xl p-6 shadow-sm border border-border">
                  <h3 className="text-xl font-semibold mb-4">Часто задаваемые вопросы</h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-base font-medium mb-2">Сколько времени занимает разработка проекта?</h4>
                      <p className="text-sm text-muted-foreground">
                        Сроки разработки зависят от сложности проекта. Обычно это от 1 до 6 месяцев. Мы всегда обсуждаем временные рамки индивидуально с каждым клиентом.
                      </p>
                    </div>
                    <div>
                      <h4 className="text-base font-medium mb-2">Как формируется стоимость разработки?</h4>
                      <p className="text-sm text-muted-foreground">
                        Стоимость проекта зависит от его сложности, объема работ и сроков. Мы предоставляем детальную смету после анализа требований и согласования технического задания.
                      </p>
                    </div>
                    <div>
                      <h4 className="text-base font-medium mb-2">Предоставляете ли вы техническую поддержку?</h4>
                      <p className="text-sm text-muted-foreground">
                        Да, мы обеспечиваем полную техническую поддержку всех наших решений. Вы можете выбрать подходящий тарифный план в зависимости от ваших потребностей.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
